export interface Marker {
  x:              number;
  y:              number;
  color:          string;
  label:          string;
  score?:         number | null;    // confidence 0–1; shown next to label when present
  is_first_image?: boolean | null;  // true = before image, false = after; null/absent = show on both
  w?:             number;           // optional box width (0-1); if BOTH w & h set → drawn as a box, else a dot
  h?:             number;           // optional box height (0-1)
}

interface DetectionCircle {
  x: number;
  y: number;
}

export interface LabelBox {
  x: number;   // box center, 0-1
  y: number;
  w: number;   // box size, 0-1
  h: number;
  label: string;
  color?: string;   // palette name (DOT_COLORS key); per-label colour like markers.
                    // Falls back to boxColor when absent.
}

interface Props {
  /** One pulsing red circle per element — replaces the old bboxX/bboxY + showDetection pair. */
  detectionCircles?: DetectionCircle[];
  markers?: Marker[];
  /** Rectangular labels from the central model, drawn as boxes with a caption. */
  boxes?: LabelBox[];
  boxColor?: string;
  imageTag?: "before" | "after";   // filter markers to this image; absent = show all
  /** Where (x,y) sits relative to the dot/circle it draws. Cloud Relabeled
   *  markers and detection_events are true box centers ("center", default).
   *  Edge labeled markers/events report the box's TOP-LEFT CORNER as x/y (the
   *  router's ei_impulse_result_bounding_box_t — see ips_ml_runner's
   *  ei_classifier_types.h). By deliberate decision that raw edge coordinate is
   *  used verbatim — the edge labels it, forwards it, nothing recomputes it —
   *  so pass "corner" for the Edge view to place the marker on that corner
   *  as-is (no centring, no nudge). */
  anchor?: "center" | "corner";
}

const DOT_COLORS: Record<string, { dot: string; text: string }> = {
  green:  { dot: "#3F9D5A", text: "#3F9D5A" },
  blue:   { dot: "#3E6E8E", text: "#6E9BB8" },
  orange: { dot: "#C77A2E", text: "#E0A12E" },
  red:    { dot: "#C7493B", text: "#D96A5E" },
  purple: { dot: "#A07D33", text: "#D8B062" },
};
const DOT_FALLBACK = { dot: "#A8A6A4", text: "#CFCDCA" };

// ── Coordinate normalisation ───────────────────────────────────────────────────
// Coordinates are stored as 0-1 fractions of the original camera image.
// The router-side Lua layer handles the classifier-pixel → image-space
// conversion before upload, so the frontend only needs to clamp to [0, 1]
// and multiply by 100 to get a CSS percentage.
//
// The overlay div is position:absolute;inset:0, so it always stretches to
// whatever size the image container is — no hardcoded resolution needed.
function toNorm(x: number, y: number): [number, number] {
  return [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))];
}

// ── Shared box style ───────────────────────────────────────────────────────────
// The single box visual used everywhere a detection box is drawn:
//   • a thin (2px) colored border rectangle,
//   • a subtle translucent fill of the same colour,
//   • the label as a small caption on the box's TOP edge — colored text on a
//     dark backing for legibility.
// Coordinates are 0-1 fractions of the centred square crop; the box is centred
// at (cx, cy) with size (w, h). Percentages resolve against the overlay root
// (position:absolute; inset:0), i.e. the image area.
function OverlayBox({
  cx, cy, w, h, color, textColor, label, score, pulse = false,
}: {
  cx: number; cy: number; w: number; h: number;
  color: string; textColor: string; label: string; score?: number | null;
  pulse?: boolean;   // this box is the anomaly one → whole rectangle pulses,
                     // with a bold red border, instead of its per-label colour
}) {
  const left = Math.max(0, (cx - w / 2) * 100);
  const top  = Math.max(0, (cy - h / 2) * 100);
  const ANOM = "#C7493B";
  return (
    <div
      className={pulse ? "anomaly-box-pulse" : undefined}
      style={{
      position: "absolute",
      left: `${left}%`,
      top: `${top}%`,
      width: `${w * 100}%`,
      height: `${h * 100}%`,
      border: pulse ? `3px solid ${ANOM}` : `2px solid ${color}`,
      background: pulse ? `${ANOM}33` : `${color}2e`,   // stronger red tint when flagged (~20%)
      borderRadius: 2,
      boxSizing: "border-box",
    }}>
      <span style={{
        position: "absolute",
        left: -2,
        bottom: "100%",          // sit on the box's top edge
        marginBottom: 1,
        fontSize: 9,
        fontWeight: 700,
        color: textColor,
        background: "rgba(0,0,0,0.65)",
        padding: "1px 4px",
        borderRadius: 2,
        whiteSpace: "nowrap",
        letterSpacing: "0.04em",
        lineHeight: 1.4,
      }}>
        {label}
        {score != null && (
          <span style={{ fontWeight: 400, opacity: 0.8, marginLeft: 3 }}>
            {Math.round(score * 100)}%
          </span>
        )}
      </span>
    </div>
  );
}

export function CoordinateOverlay({
  detectionCircles = [],
  markers = [],
  boxes = [],
  boxColor = "#D8B062",
  imageTag,
  anchor = "center",
}: Props) {
  // Show only markers for this image; show on both if is_first_image is null/absent.
  const visibleMarkers = imageTag
    ? markers.filter((m) => {
        if (m.is_first_image == null) return true;
        return imageTag === "before" ? m.is_first_image : !m.is_first_image;
      })
    : markers;

  // Both the dot and the pulse ring take this same transform. "center" centres
  // the element on the coordinate (Cloud markers/events are true box centres).
  // "corner" (Edge view) places the element's top-left AT the coordinate with
  // NO adjustment: the router reports the box's top-left corner as x/y, and by
  // deliberate decision the coordinate is used verbatim end-to-end — the edge
  // labels it, forwards it, nothing recomputes it, and the frontend just drops
  // the marker on that corner. (The `.detection-pulse` keyframes animate the
  // `scale` property, not `transform`, so this inline transform is honoured for
  // the ring too — otherwise the animation would override it and re-centre the
  // ring off the corner.)
  const anchorTransform =
    anchor === "corner" ? "none" : "translate(-50%, -50%)";

  // An anomaly event (detectionCircles) no longer draws its own separate red
  // ring — instead the marker it sits on IS the highlight: the Cloud box's
  // frame pulses, the Edge dot pulses with a red border. A marker is the
  // anomaly one when an event coordinate lands on it (same coordinate space:
  // both are box centres in Cloud, both are corners in Edge).
  const ANOM_MATCH = 0.06;   // normalised distance; objects sit further apart
  const isAnomaly = (mx: number, my: number) =>
    detectionCircles.some((c) => {
      const [cx, cy] = toNorm(c.x, c.y);
      return Math.hypot(cx - mx, cy - my) <= ANOM_MATCH;
    });

  // Fallback: an event that lands on no visible marker (e.g. markers toggled
  // off, or a coordinate that didn't line up) keeps its own pulsing ring, so
  // the anomaly is never left unmarked.
  const orphanCircles = detectionCircles.filter((c) => {
    const [cx, cy] = toNorm(c.x, c.y);
    return !visibleMarkers.some((m) => {
      const [mx, my] = toNorm(m.x, m.y);
      return Math.hypot(cx - mx, cy - my) <= ANOM_MATCH;
    });
  });

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {/* Fallback rings for anomaly events with no marker to pulse. */}
      {orphanCircles.map((c, i) => {
        const [cx, cy] = toNorm(c.x, c.y).map((v) => v * 100) as [number, number];
        return (
          <div
            key={`orphan-${i}`}
            className="detection-pulse"
            style={{
              position: "absolute",
              left: `${cx}%`,
              top: `${cy}%`,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "transparent",
              border: "3px solid #C7493B",
              transform: anchorTransform,
            }}
          />
        );
      })}

      {/* Category markers — a BOX when the marker carries both w & h, else a dot */}
      {visibleMarkers.map((m, i) => {
        const colors = DOT_COLORS[m.color] ?? DOT_FALLBACK;
        const [mx, my] = toNorm(m.x, m.y);
        const anom = isAnomaly(mx, my);
        if (m.w != null && m.h != null) {
          return (
            <OverlayBox
              key={i}
              cx={mx} cy={my} w={m.w} h={m.h}
              color={colors.dot} textColor={colors.text}
              label={m.label} score={m.score}
              pulse={anom}
            />
          );
        }
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${mx * 100}%`,
              top: `${my * 100}%`,
              transform: anchorTransform,
            }}
          >
            {/* Anomaly dot pulses (scales) and gets a red border instead of the
                usual white one, so it reads as the flagged event without a
                separate off-centre ring. */}
            <div
              className={anom ? "detection-pulse" : undefined}
              style={{
                width: 13,
                height: 13,
                borderRadius: "50%",
                background: colors.dot,
                border: anom ? "2px solid #C7493B" : "2px solid rgba(255,255,255,0.85)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
            }} />
            <span style={{
              position: "absolute",
              top: "calc(100% + 2px)",
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 9,
              fontWeight: 700,
              color: colors.text,
              background: "rgba(0,0,0,0.65)",
              padding: "1px 4px",
              borderRadius: 2,
              whiteSpace: "nowrap",
              letterSpacing: "0.04em",
              lineHeight: 1.4,
            }}>
              {m.label}
              {m.score != null && (
                <span style={{ fontWeight: 400, opacity: 0.8, marginLeft: 3 }}>
                  {Math.round(m.score * 100)}%
                </span>
              )}
            </span>
          </div>
        );
      })}

      {/* Rectangular labels from the central model — same shared box style.
          Per-label colour (like the markers) when the box carries one; else the
          single boxColor. */}
      {boxes.map((b, i) => {
        const [cx, cy] = toNorm(b.x, b.y);
        const colors = b.color ? (DOT_COLORS[b.color] ?? DOT_FALLBACK)
                               : { dot: boxColor, text: boxColor };
        return (
          <OverlayBox
            key={`box-${i}`}
            cx={cx} cy={cy} w={b.w} h={b.h}
            color={colors.dot} textColor={colors.text}
            label={b.label}
          />
        );
      })}
    </div>
  );
}
